import { useState } from "react";
import { Form } from "react-bootstrap";
import { ContactFieldsProps } from "./types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "components/Button";
import cn from "classnames";
import useMediaQuery from "components/utils/useMediaQuery";
import { REQUIRED_LAST_AND_FIRST_OR_CONTACT } from "components/utils/validation";
import { isEmpty, omit } from "lodash";
import { useStates } from "components/utils/useStates";
import AddressTypeahead from "./AddressTypeahead";
import { mapMelissaAddressToContactFields, MelissaApiAddress } from "./useAddressLookup";

const ContactFields = ({
    idPrefix,
    formData,
    onChange,
    requiredFields,
    errors,
    hiddenFields,
    onClearForm,
    lotLabel,
    needsFirstAndLastOrCompany,
}: ContactFieldsProps) => {
    const isMobile = useMediaQuery("(max-width: 768px)");
    const [states = []] = useStates();

    const addressHidden = hiddenFields.includes("address");

    const isFormEmpty = !Object.values(omit(formData, ["storedContactNumber", "origId", "contactType", "saveContact", "copyContact"])).some(
        (v) => !isEmpty(v)
    );

    const [manualRevealed, setManualRevealed] = useState(false);
    const hasAddressData = Boolean(formData?.address_cont || formData?.city || formData?.state || formData?.zip);
    const hasAddressErrors = Boolean(errors?.address_cont || errors?.city || errors?.state || errors?.zip);
    const hasVisibleAddressSubFields = ["address_cont", "city", "state", "zip"].some((f) => !hiddenFields.includes(f));
    const showAddressBlock = (addressHidden || manualRevealed || hasAddressData || hasAddressErrors) && hasVisibleAddressSubFields;

    const handleAddressSelect = (selected: MelissaApiAddress) => {
        onChange(mapMelissaAddressToContactFields(selected, states));
        setManualRevealed(true);
    };

    return (
        <div className="m-3 border rounded-8 overflow-hidden">
            <div className="d-flex flex-column gap-3 bg-white pt-3 px-3">
                <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between gap-3 py-2">
                    {needsFirstAndLastOrCompany && (
                        <div className="d-flex align-items-center lh-sm">
                            <span className="me-2">
                                <FontAwesomeIcon icon="circle-info" />
                            </span>
                            <span>{REQUIRED_LAST_AND_FIRST_OR_CONTACT}</span>
                        </div>
                    )}
                    <Button className="ms-sm-auto flex-shrink-0" variant="secondary" onClick={onClearForm} disabled={isFormEmpty}>
                        Clear
                    </Button>
                </div>

                <div className="gap-2 d-flex flex-column">
                    {!hiddenFields.includes("firstname") && (
                        <ContactField
                            idPrefix={idPrefix}
                            name="firstName"
                            label="First Name"
                            value={formData?.firstName}
                            error={errors?.firstName}
                            required={requiredFields.includes("firstname")}
                            onChange={(v) => onChange({ firstName: v })}
                            isMobile={isMobile}
                            placeholder="e.g., John"
                        />
                    )}

                    {!hiddenFields.includes("lastname") && (
                        <ContactField
                            idPrefix={idPrefix}
                            name="lastName"
                            label="Last Name"
                            value={formData?.lastName}
                            error={errors?.lastName}
                            required={requiredFields.includes("lastname")}
                            onChange={(v) => onChange({ lastName: v })}
                            isMobile={isMobile}
                            placeholder="e.g., Smith"
                        />
                    )}

                    {!hiddenFields.includes("company") && (
                        <ContactField
                            idPrefix={idPrefix}
                            name="company"
                            label="Company"
                            value={formData?.company}
                            error={errors?.company}
                            required={requiredFields.includes("company")}
                            onChange={(v) => onChange({ company: v })}
                            isMobile={isMobile}
                            placeholder="e.g., Acme Corp"
                        />
                    )}

                    {(!addressHidden || showAddressBlock || ["email", "phone", "cell", "fax"].some((f) => !hiddenFields.includes(f))) && (
                        <hr />
                    )}

                    {!addressHidden && (
                        <AddressTypeahead
                            idPrefix={idPrefix}
                            label="Address"
                            value={formData?.address}
                            error={errors?.address}
                            required={requiredFields.includes("address")}
                            onChange={(v) => onChange({ address: v })}
                            onSelect={handleAddressSelect}
                            onManualEntry={() => setManualRevealed(true)}
                            addressFieldsVisible={showAddressBlock}
                            isMobile={isMobile}
                            placeholder="Start typing an address, e.g. 123 Main..."
                        />
                    )}
                    <div
                        className={cn({
                            "d-flex align-items-start": !isMobile,
                            "d-none": !showAddressBlock,
                        })}
                    >
                        {addressHidden && (
                            <Form.Label className="mb-1 pt-2" style={isMobile ? undefined : { width: "168px" }}>
                                Address
                            </Form.Label>
                        )}
                        <div
                            className={cn("rounded bg-light px-3 py-3 mb-3 mt-2", {
                                "w-75 ms-auto": !isMobile,
                                "w-100": isMobile,
                            })}
                        >
                            {!hiddenFields.includes("address_cont") && (
                                <ContactField
                                    idPrefix={idPrefix}
                                    name="address_cont"
                                    label="Address (cont)"
                                    value={formData?.address_cont}
                                    error={errors?.address_cont}
                                    required={requiredFields.includes("address_cont")}
                                    onChange={(v) => onChange({ address_cont: v })}
                                    isMobile={isMobile}
                                    className="mb-3"
                                    placeholder="Enter Contact Address"
                                />
                            )}
                            <div className="d-flex flex-column gap-3">
                                {!hiddenFields.includes("city") && (
                                    <ContactField
                                        idPrefix={idPrefix}
                                        name="city"
                                        label="City"
                                        value={formData?.city}
                                        error={errors?.city}
                                        required={requiredFields.includes("city")}
                                        onChange={(v) => onChange({ city: v })}
                                        isMobile={isMobile}
                                        placeholder="Enter Contact City"
                                    />
                                )}
                                {!hiddenFields.includes("state") && (
                                    <Form.Group controlId={`${idPrefix}-state`} className={cn({ "d-flex flex-column mb-2": isMobile })}>
                                        <div className={cn({ "d-flex justify-content-between align-items-center": !isMobile })}>
                                            <Form.Label
                                                style={{ width: "168px" }}
                                                className={cn("text-truncate mb-0 d-block", { "mb-1": isMobile })}
                                            >
                                                State/Province
                                                {requiredFields.includes("state") && <span aria-hidden="true">&thinsp;*</span>}
                                            </Form.Label>
                                            <div className={cn({ "w-75": !isMobile, "w-100": isMobile })}>
                                                <Form.Select
                                                    value={formData?.state ?? ""}
                                                    onChange={(e) => onChange({ state: e.target.value })}
                                                    aria-describedby={errors?.state ? `${idPrefix}-state-error` : undefined}
                                                    aria-invalid={Boolean(errors?.state)}
                                                    required={requiredFields.includes("state")}
                                                    isInvalid={Boolean(errors?.state)}
                                                >
                                                    <option value="">Select...</option>
                                                    {states.map((s) => (
                                                        <option key={s.state} value={s.abbreviation}>
                                                            {s.state}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </div>
                                        </div>
                                        {errors?.state && (
                                            <div className={cn({ "d-flex justify-content-between align-items-center": !isMobile })}>
                                                {!isMobile && <div style={{ width: "168px" }} />}
                                                <div className={cn({ "w-75": !isMobile })}>
                                                    <Form.Control.Feedback
                                                        id={`${idPrefix}-state-error`}
                                                        type="invalid"
                                                        className="d-block"
                                                    >
                                                        {errors.state}
                                                    </Form.Control.Feedback>
                                                </div>
                                            </div>
                                        )}
                                    </Form.Group>
                                )}
                                {!hiddenFields.includes("zip") && (
                                    <ContactField
                                        idPrefix={idPrefix}
                                        name="zip"
                                        label="Postal Code"
                                        value={formData?.zip}
                                        error={errors?.zip}
                                        required={requiredFields.includes("zip")}
                                        onChange={(v) => onChange({ zip: v })}
                                        isMobile={isMobile}
                                        pattern={/^\d{5}(?:[-]\d{4})?$|^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/.source}
                                        placeholder="Enter Contact Postal Code"
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {!hiddenFields.includes("email") && (
                        <ContactField
                            idPrefix={idPrefix}
                            name="email"
                            label="Email"
                            value={formData?.email}
                            error={errors?.email}
                            required={requiredFields.includes("email")}
                            onChange={(v) => onChange({ email: v })}
                            isMobile={isMobile}
                            placeholder="you@example.com"
                        />
                    )}

                    {!hiddenFields.includes("phone") && (
                        <ContactField
                            idPrefix={idPrefix}
                            name="phone"
                            label="Phone"
                            value={formData?.phone}
                            error={errors?.phone}
                            required={requiredFields.includes("phone")}
                            onChange={(v) => onChange({ phone: v })}
                            isMobile={isMobile}
                            placeholder="(555) 123-4567"
                        />
                    )}

                    {!hiddenFields.includes("cell") && (
                        <ContactField
                            idPrefix={idPrefix}
                            name="cell"
                            label="Cell"
                            value={formData?.cell}
                            error={errors?.cell}
                            required={requiredFields.includes("cell")}
                            onChange={(v) => onChange({ cell: v })}
                            isMobile={isMobile}
                            placeholder="(555) 123-4567"
                        />
                    )}

                    {!hiddenFields.includes("fax") && (
                        <ContactField
                            idPrefix={idPrefix}
                            name="fax"
                            label="Fax"
                            value={formData?.fax}
                            error={errors?.fax}
                            required={requiredFields.includes("fax")}
                            onChange={(v) => onChange({ fax: v })}
                            isMobile={isMobile}
                            placeholder="(555) 123-4567"
                        />
                    )}

                    {["acct_number", "taxid", "premiseid", "meterid", "lot"].some((f) => !hiddenFields.includes(f)) && <hr />}

                    {!hiddenFields.includes("acct_number") && (
                        <ContactField
                            idPrefix={idPrefix}
                            name="acct_number"
                            label="Account Number"
                            value={formData?.acct_number}
                            error={errors?.acct_number}
                            required={requiredFields.includes("acct_number")}
                            onChange={(v) => onChange({ acct_number: v })}
                            isMobile={isMobile}
                            placeholder="Enter your account number"
                        />
                    )}

                    {!hiddenFields.includes("taxid") && (
                        <ContactField
                            idPrefix={idPrefix}
                            name="taxId"
                            label="Tax ID"
                            value={formData?.taxId}
                            error={errors?.taxId}
                            required={requiredFields.includes("taxid")}
                            onChange={(v) => onChange({ taxId: v })}
                            isMobile={isMobile}
                            placeholder="XX-XXXXXXX"
                        />
                    )}

                    {!hiddenFields.includes("premiseid") && (
                        <ContactField
                            idPrefix={idPrefix}
                            name="premiseId"
                            label="Premise ID"
                            value={formData?.premiseId}
                            error={errors?.premiseId}
                            required={requiredFields.includes("premiseid")}
                            onChange={(v) => onChange({ premiseId: v })}
                            isMobile={isMobile}
                            placeholder="Enter premise identifier"
                        />
                    )}

                    {!hiddenFields.includes("meterid") && (
                        <ContactField
                            idPrefix={idPrefix}
                            name="meterId"
                            label="Meter ID"
                            value={formData?.meterId}
                            error={errors?.meterId}
                            required={requiredFields.includes("meterid")}
                            onChange={(v) => onChange({ meterId: v })}
                            isMobile={isMobile}
                            placeholder="Enter meter number"
                        />
                    )}

                    {!hiddenFields.includes("lot") && (
                        <ContactField
                            idPrefix={idPrefix}
                            name="lot"
                            label={lotLabel}
                            value={formData?.lot}
                            error={errors?.lot}
                            required={requiredFields.includes("lot")}
                            onChange={(v) => onChange({ lot: v })}
                            isMobile={isMobile}
                            placeholder={`Enter ${lotLabel}`}
                        />
                    )}
                </div>
            </div>

            <Form.Group className="border-top mt-3 bg-light px-3 py-2">
                <Form.Check type="switch" id={`${idPrefix}-save-as-new-contact`} className="d-flex align-items-center gap-2 m-0 ps-0">
                    <Form.Check.Input
                        type="checkbox"
                        checked={formData?.saveContact}
                        onChange={(e) => onChange({ saveContact: e.target.checked })}
                        className="m-0 flex-shrink-0"
                    />
                    <Form.Check.Label className={cn({ "lh-sm": isMobile })}>Save as a new contact in my contact list</Form.Check.Label>
                </Form.Check>
            </Form.Group>
        </div>
    );
};

interface ContactFieldProps {
    idPrefix: string;
    name: string;
    label: string;
    value: string | undefined;
    error?: string;
    required?: boolean;
    onChange: (value: string) => void;
    isMobile?: boolean;
    pattern?: string;
    className?: string;
    placeholder?: string;
}

const ContactField = ({
    idPrefix,
    name,
    label,
    value,
    error,
    required,
    onChange,
    isMobile,
    pattern,
    className,
    placeholder,
}: ContactFieldProps) => {
    const controlId = `${idPrefix}-${name}`;
    const errorId = `${controlId}-error`;
    return (
        <Form.Group controlId={controlId} className={cn(className, { "d-flex flex-column mb-2": isMobile })}>
            <div className={cn({ "d-flex justify-content-between align-items-center": !isMobile })}>
                <Form.Label style={{ width: "168px" }} className={cn("text-truncate mb-0 d-block", { "mb-1": isMobile })}>
                    {label}
                    {required && <span aria-hidden="true">&thinsp;*</span>}
                </Form.Label>
                <div className={cn({ "w-75": !isMobile, "w-100": isMobile })}>
                    <Form.Control
                        className={cn({ "field-error": Boolean(error) })}
                        type="text"
                        value={value ?? ""}
                        isInvalid={Boolean(error)}
                        onChange={(e) => onChange(e.target.value)}
                        aria-describedby={error ? errorId : undefined}
                        aria-invalid={Boolean(error)}
                        required={required}
                        aria-required={required}
                        pattern={pattern}
                        placeholder={placeholder}
                    />
                </div>
            </div>
            {error && (
                <div className={cn({ "d-flex justify-content-between align-items-center": !isMobile })}>
                    {!isMobile && <div style={{ width: "168px" }} />}
                    <div className={cn({ "w-75": !isMobile })}>
                        <Form.Control.Feedback id={errorId} type="invalid" className="d-block">
                            {error}
                        </Form.Control.Feedback>
                    </div>
                </div>
            )}
        </Form.Group>
    );
};
export default ContactFields;
